import { useEffect, useState } from 'react';
import type { LeaderRow } from '../types';
import { fetchLeaderboard } from '../lib/api';
import Avatar from './Avatar';

const SHOWN = 6;

/** Equal counts share a place, so two people on eleven cards are both third. */
function ranked(rows: LeaderRow[]) {
  let place = 0;
  let previous: number | null = null;
  return rows.map((row, i) => {
    if (row.cards !== previous) { place = i + 1; previous = row.cards; }
    return { row, place };
  });
}

export default function Leaderboard({
  meId, refreshKey,
}: { meId: string; refreshKey: number }) {
  const [rows, setRows] = useState<LeaderRow[] | null>(null);

  useEffect(() => {
    let alive = true;
    fetchLeaderboard()
      .then((r) => { if (alive) setRows(r); })
      .catch(() => { if (alive) setRows([]); });
    return () => { alive = false; };
  }, [refreshKey]);

  if (!rows) return <p className="lb-note">Counting…</p>;
  if (!rows.length) {
    return <p className="lb-note">Nobody has had work approved yet. Be first.</p>;
  }

  const all = ranked(rows);
  const top = all.slice(0, SHOWN);
  // Being ranked ninth and shown nothing is worse than not having a board at
  // all, so anyone below the fold still gets their own line.
  const mine = all.find((r) => r.row.member_id === meId);
  const below = mine && !top.some((r) => r.row.member_id === meId) ? mine : null;

  const line = ({ row, place }: { row: LeaderRow; place: number }) => (
    <div
      key={row.member_id}
      className={`lb-row${row.member_id === meId ? ' is-me' : ''}`}
      title={[
        `${row.cards} card${row.cards === 1 ? '' : 's'} approved`,
        `${row.fixes} fixed`,
        `${row.shipped} shipped`,
        row.waiting ? `${row.waiting} waiting on a decision` : null,
      ].filter(Boolean).join(' · ')}
    >
      <span className={`lb-place${place <= 3 ? ` m${place}` : ''}`}>{place}</span>
      <Avatar name={row.display_name} url={row.avatar_url} size={20} />
      <span className="lb-name">{row.display_name}</span>
      {row.waiting > 0 && <span className="lb-wait">+{row.waiting}</span>}
      <span className="lb-n">{row.cards}</span>
    </div>
  );

  return (
    <div className="lb">
      {top.map(line)}
      {below && (
        <>
          <div className="lb-gap" />
          {line(below)}
        </>
      )}
      <p className="lb-note">
        Cards the owner has approved. Anything still waiting on him shows as
        <span className="lb-wait"> +n</span>.
      </p>
    </div>
  );
}
